import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateVerificationQueries } from "@/utils/verificationQueryInvalidation";

export function useFlutterwaveVerification() {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    const verifyPayment = async (
      reference: string,
      transaction_id?: string,
      status?: string,
    ) => {
      if (!user?.id || isVerifying || isVerified) return;

      // Store a verification marker in sessionStorage to prevent duplicate verifications
      const verificationKey = `flw-verification-${reference}-${transaction_id || ""}`;
      const alreadyVerifying = sessionStorage.getItem(verificationKey);
      if (alreadyVerifying === "true") {
        console.log(
          `🟣 [FLUTTERWAVE_VERIFICATION] Skipping duplicate verification for ${verificationKey}`,
        );
        return;
      }

      sessionStorage.setItem(verificationKey, "true");
      setIsVerifying(true);

      try {
        console.log(
          `🟣 [FLUTTERWAVE_VERIFICATION] Verifying payment: ${reference}, status: ${status} for user: ${user.id}`,
        );

        // Accept both 'successful' and 'completed' as valid payment statuses
        const validStatuses = ["successful", "completed"];

        // If we received status and it's not in valid statuses, show message but don't verify
        if (status && !validStatuses.includes(status.toLowerCase())) {
          console.error(
            `Payment status not valid: ${status}, expected one of: ${validStatuses.join(", ")}`,
          );
          toast({
            title: "Payment Failed",
            description: `Payment was not successful: ${status}`,
            variant: "destructive",
          });
          setIsVerifying(false);
          sessionStorage.removeItem(verificationKey);

          // If this was a verification payment, navigate back to verification page
          if (
            location.pathname.includes("verification") ||
            location.search.includes("verification=true")
          ) {
            setTimeout(() => {
              navigate("/verification", { replace: true });
            }, 1500);
          }
          return;
        }

        // Check if this is a verification payment
        const params = new URLSearchParams(location.search);
        const isVerification =
          params.get("verification") === "true" ||
          location.pathname.includes("verification/success");

        console.log(
          `🟣 [FLUTTERWAVE_VERIFICATION] Payment type: ${isVerification ? "verification" : "wallet top-up"}`,
        );

        // Use the Flutterwave payment endpoint for verification
        const body = {
          action: "verify",
          tx_ref: reference,
          userId: user.id,
          verification: isVerification,
        };

        console.log(
          "🟣 [FLUTTERWAVE_VERIFICATION] Calling verification with body:",
          body,
        );

        // Call the Flutterwave edge function to verify the payment
        const { data, error } = await supabase.functions.invoke(
          "flutterwave-payment",
          {
            body,
          },
        );

        if (error) {
          console.error(
            "🟣 [FLUTTERWAVE_VERIFICATION] Supabase function error:",
            error,
          );
          toast({
            title: "Verification Failed",
            description:
              "Failed to verify payment. Please contact support with your reference number.",
            variant: "destructive",
          });
          setIsVerifying(false);
          sessionStorage.removeItem(verificationKey);
          return;
        }

        console.log(
          "🟣 [FLUTTERWAVE_VERIFICATION] Payment verification response:",
          data,
        );

        if (data?.success === true) {
          if (isVerification) {
            toast({
              title: "Verification Successful",
              description:
                data.message ||
                "Verification successful! Your profile has been verified for 1 year.",
              variant: "default",
            });
            // Invalidate every verification-dependent query so the dashboard,
            // sidebar badge and gated features update immediately.
            invalidateVerificationQueries(queryClient, user?.id);
            queryClient.invalidateQueries({ queryKey: ["artistProfile"] });
            queryClient.invalidateQueries({ queryKey: ["sponsorProfile"] });

            // Redirect to dashboard with success parameter after verification
            setTimeout(() => {
              navigate(
                `/dashboard/${userRole === "artist" ? "artist" : "sponsor"}?verification_success=true`,
                { replace: true },
              );
            }, 1500);
          } else {
            // Enhanced success message for wallet top-up
            const amountAdded = data.data?.amount || "unknown amount";
            toast({
              title: "Payment Successful",
              description: `Wallet top-up successful! ₦${amountAdded?.toLocaleString ? amountAdded.toLocaleString() : amountAdded} has been added to your wallet.`,
              variant: "default",
            });

            // Enhanced query invalidation for wallet data with more aggressive refreshing
            console.log(
              "🔄 [FLUTTERWAVE_VERIFICATION] Invalidating wallet and transaction queries...",
            );
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["sponsorWallet"] }),
              queryClient.invalidateQueries({ queryKey: ["transactions"] }),
              queryClient.invalidateQueries({
                queryKey: ["adminTransactions"],
              }),
              queryClient.invalidateQueries({ queryKey: ["wallet"] }),
              queryClient.invalidateQueries({ queryKey: ["sponsorStatsData"] }),
              queryClient.invalidateQueries({ queryKey: ["sponsorData"] }),
              queryClient.invalidateQueries({
                queryKey: ["userWallet", user.id],
              }),
            ]);

            // Force refresh queries with a slightly longer delay to ensure DB changes are committed
            setTimeout(() => {
              queryClient.refetchQueries({ queryKey: ["transactions"] });
              queryClient.refetchQueries({ queryKey: ["sponsorWallet"] });
              queryClient.refetchQueries({ queryKey: ["userWallet", user.id] });
            }, 1500);

            // Redirect to dashboard
            setTimeout(() => {
              navigate(
                `/dashboard/${userRole === "artist" ? "artist" : "sponsor"}?payment_success=true`,
                { replace: true },
              );
            }, 2000);
          }

          setIsVerified(true);
        } else {
          console.log(
            "🟣 [FLUTTERWAVE_VERIFICATION] Payment not successful:",
            data,
          );

          // Handle session mismatch specially
          if (data?.data?.session_mismatch) {
            toast({
              title: "Session Mismatch",
              description:
                data.message ||
                "Please ensure you're logged in with the same account that started the payment.",
              variant: "destructive",
            });

            // Provide guidance for session mismatch
            setTimeout(() => {
              toast({
                title: "Troubleshooting Tip",
                description:
                  "Try logging out and back in, then check your transaction history or contact support.",
                variant: "default",
              });
            }, 3000);
          } else {
            // Enhanced error message with more details
            const errorDetails = data?.error_detail
              ? ` (${data.error_detail})`
              : "";
            toast({
              title: "Verification Failed",
              description:
                data?.message + errorDetails ||
                "Payment verification failed. Please contact support with your reference number.",
              variant: "destructive",
            });
          }

          // Redirect back to verification page on failure if it's a verification
          if (isVerification) {
            setTimeout(() => {
              navigate("/verification", { replace: true });
            }, 1500);
          }
        }
      } catch (err) {
        console.error(
          "🟣 [FLUTTERWAVE_VERIFICATION] Error during payment verification:",
          err,
        );
        toast({
          title: "Error",
          description:
            "An unexpected error occurred while verifying your payment. Please contact support.",
          variant: "destructive",
        });

        // Redirect back to verification page on error if it's a verification
        if (location.pathname.includes("verification")) {
          setTimeout(() => {
            navigate("/verification", { replace: true });
          }, 1500);
        }
      } finally {
        setIsVerifying(false);
        sessionStorage.removeItem(verificationKey);

        // Clear the URL parameters after verification
        if (!isVerified && location.search) {
          const cleanPath = location.pathname;
          window.history.replaceState({}, document.title, cleanPath);
        }
      }
    };

    // Extract reference, transaction_id, and status from URL if present
    const params = new URLSearchParams(location.search);
    const reference = params.get("tx_ref") || params.get("ref");
    const transaction_id = params.get("transaction_id");
    const status = params.get("status");
    const gateway = params.get("gateway");
    const paymentSuccess = params.get("payment_success");

    // Primary path: explicit reference
    const hasExplicitFlwRef =
      reference &&
      (reference.startsWith("flw-") ||
        params.has("tx_ref") ||
        gateway === "flutterwave");

    if (hasExplicitFlwRef && user?.id) {
      console.log(
        `🟣 [FLUTTERWAVE_VERIFICATION] Found Flutterwave payment parameters: reference=${reference}, transaction_id=${transaction_id}, status=${status}`,
      );
      verifyPayment(
        reference!,
        transaction_id || undefined,
        status || undefined,
      );
      return;
    }

    // Fallback path: redirect arrived without tx_ref. Look up the latest pending
    // flw- initiation for this user and verify by its reference.
    const isFlwRedirectWithoutRef =
      !reference &&
      user?.id &&
      (gateway === "flutterwave" ||
        (paymentSuccess === "true" && !params.get("trxref")));

    if (isFlwRedirectWithoutRef && !isVerifying && !isVerified) {
      console.log(
        `🟣 [FLUTTERWAVE_VERIFICATION] Flutterwave redirect with no tx_ref — looking up latest pending initiation`,
      );
      (async () => {
        try {
          const thirtyMinAgo = new Date(
            Date.now() - 30 * 60 * 1000,
          ).toISOString();
          const { data: rows, error } = await supabase
            .from("payment_initiations")
            .select("reference")
            .eq("user_id", user.id)
            .like("reference", "flw-%")
            .gte("created_at", thirtyMinAgo)
            .order("created_at", { ascending: false })
            .limit(1);
          if (error) {
            console.error(
              "🟣 [FLUTTERWAVE_VERIFICATION] Fallback lookup error:",
              error,
            );
            return;
          }
          const fallbackRef = rows?.[0]?.reference;
          if (fallbackRef) {
            console.log(
              `🟣 [FLUTTERWAVE_VERIFICATION] Falling back to reference: ${fallbackRef}`,
            );
            verifyPayment(fallbackRef, undefined, "successful");
          } else {
            console.log(
              "🟣 [FLUTTERWAVE_VERIFICATION] No recent pending Flutterwave initiation found",
            );
          }
        } catch (e) {
          console.error("🟣 [FLUTTERWAVE_VERIFICATION] Fallback exception:", e);
        }
      })();
    }
  }, [
    location.search,
    location.pathname,
    user,
    queryClient,
    isVerifying,
    isVerified,
    navigate,
    userRole,
  ]);

  return { isVerifying, isVerified };
}
